/**
 * PrivilegesListPage — Liste publique des Escales Privileges (offres partenaires).
 * - Visiteur / non-membre : teaser (image + titre + accroche + catégorie) + CTA adhésion
 * - Adhérent actif : détail complet (prix membre, économies, bouton "Voir l'offre")
 * Filtres par catégorie.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPublicPrivileges, getMedia } from "../lib/api";
import { getPublicThumbnail } from "../lib/mediaUrl";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Sparkles, Lock, Calendar, Tag, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const CATEGORIES = [
  { value: "", label: "Toutes" },
  { value: "croisieres", label: "Croisières" },
  { value: "hotels", label: "Hôtels" },
  { value: "sejours", label: "Séjours" },
  { value: "excursions", label: "Excursions" },
  { value: "accessoires", label: "Accessoires" },
  { value: "bagagerie", label: "Bagagerie" },
  { value: "autre", label: "Autre" },
];

function useMediaThumb(mediaId) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!mediaId) { setUrl(null); return; }
    let alive = true;
    getMedia(mediaId).then((res) => {
      if (alive) setUrl(getPublicThumbnail(res.data, 800));
    }).catch(() => alive && setUrl(null));
    return () => { alive = false; };
  }, [mediaId]);
  return url;
}

function PrivilegeCard({ offer, isMember }) {
  const thumb = useMediaThumb(offer.main_image_id);
  const endDate = offer.end_date ? format(new Date(offer.end_date), "dd MMM yyyy", { locale: fr }) : null;
  const categoryLabel = CATEGORIES.find(c => c.value === offer.category)?.label || offer.category;

  return (
    <Link
      to={`/privileges/${offer.id}`}
      data-testid={`privilege-card-${offer.id}`}
      className="group block bg-white border border-border rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[16/10] bg-secondary overflow-hidden">
        {thumb ? (
          <img src={thumb} alt={offer.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <Sparkles className="h-12 w-12 opacity-20" />
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <Badge className="bg-primary/90 text-white border-0 backdrop-blur-sm">
            <Sparkles className="h-3 w-3 mr-1" />
            Escale Privilège
          </Badge>
        </div>
        {categoryLabel && (
          <Badge variant="secondary" className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm">
            <Tag className="h-3 w-3 mr-1" />
            {categoryLabel}
          </Badge>
        )}
      </div>

      <div className="p-5 space-y-3">
        <h3 className="font-heading text-xl font-semibold text-primary line-clamp-2 min-h-[3.5rem]">{offer.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">{offer.hook}</p>

        {isMember && !offer.is_teaser ? (
          <div className="pt-2 border-t border-border/60 space-y-2">
            {offer.member_price != null && offer.public_price != null && offer.savings_percent > 0 && (
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-primary">{offer.member_price.toFixed(2)} €</span>
                <span className="text-sm text-muted-foreground line-through">{offer.public_price.toFixed(2)} €</span>
                <Badge className="bg-green-100 text-green-700 border-green-200 ml-auto">
                  -{offer.savings_percent}%
                </Badge>
              </div>
            )}
            {endDate && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Valable jusqu'au {endDate}
              </p>
            )}
          </div>
        ) : (
          <div className="pt-2 border-t border-border/60 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-primary/80">
              <Lock className="h-4 w-4" /> Réservée aux adhérents
            </div>
            {endDate && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Valable jusqu'au {endDate}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-primary font-medium group-hover:underline">
            Voir l'offre
          </span>
          <ArrowRight className="h-4 w-4 text-primary group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

export default function PrivilegesListPage() {
  const { user, isMember } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = category ? { category } : {};
    getPublicPrivileges(params)
      .then((res) => setOffers(res.data || []))
      .catch(() => setOffers([]))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <div className="min-h-screen bg-secondary pt-24 pb-16" data-testid="privileges-list-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="mb-10 text-center">
          <Badge className="bg-primary/10 text-primary border-primary/20 mb-4">
            <Sparkles className="h-3 w-3 mr-1" />
            Réservé aux adhérents ITERCATUS
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-4">
            Escales Privilèges
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Des offres exclusives négociées avec nos partenaires voyage&nbsp;: croisières, hôtels, séjours,
            excursions et bien plus, à des tarifs privilégiés.
          </p>
        </div>

        {/* CTA Visiteur */}
        {!isMember && (
          <div className="mb-10 bg-gradient-to-br from-primary to-primary/80 text-white rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 shadow-lg">
            <div className="flex-1 text-center md:text-left">
              <h2 className="font-heading text-lg md:text-xl font-semibold mb-2">
                Débloquez toutes les offres privilégiées
              </h2>
              <p className="text-sm md:text-base text-white/90">
                Rejoignez ITERCATUS pour accéder aux codes promo, prix membres et instructions de réservation exclusives.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <Link to="/adhesion" data-testid="privileges-cta-adhesion">
                <Button variant="secondary" className="w-full sm:w-auto bg-white text-primary hover:bg-white/90">
                  Devenir adhérent
                </Button>
              </Link>
              {!user && (
                <Link to="/connexion" data-testid="privileges-cta-login">
                  <Button variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10">
                    Se connecter
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="mb-8 overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
          <div className="flex gap-2 min-w-max md:flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                data-testid={`privileges-filter-${c.value || 'all'}`}
                onClick={() => setCategory(c.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  category === c.value
                    ? "bg-primary text-white shadow-sm"
                    : "bg-white border border-border text-muted-foreground hover:text-primary hover:border-primary"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Liste */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-96 rounded-2xl" />
            ))}
          </div>
        ) : offers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-border">
            <Sparkles className="h-16 w-16 mx-auto text-muted-foreground/40 mb-4" />
            <p className="text-lg text-muted-foreground">Aucune offre disponible pour le moment.</p>
            <p className="text-sm text-muted-foreground mt-2">Revenez bientôt&nbsp;!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="privileges-grid">
            {offers.map((o) => (
              <PrivilegeCard key={o.id} offer={o} isMember={isMember} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
